/**
 * Qual arquivo do item é a faixa que se procura.
 *
 * Mora separado do `acervo.ts` de propósito: ali é a conversa com o Internet
 * Archive, aqui é só a regra de decidir. A regra é a parte que erra calada --
 * escolher o arquivo errado não estoura nada, só toca outra música com o nome
 * da certa -- então é a parte que tem prova
 * (`packages/shared/test/gravacao.mjs`), e prova que roda sem rede nenhuma.
 */

import type { Faixa } from '../livros/audiolivro';
/* Tipo, e só tipo: some na compilação, então o `acervo.ts` continua importando
   este arquivo sem que o de lá volte para cá em tempo de execução -- e a prova
   segue rodando com `node` cru, sem empacotar nada. */
import type { EmCartaz } from './acervo';

/**
 * O texto na forma de comparar: sem acento, sem pontuação, tudo junto.
 *
 * `Gymnopédie No. 1` no Last.fm e `Gymnopedie No.1` no arquivo do acervo são a
 * mesma faixa, e só não são iguais por causa do acento e do espaço.
 */
export function achatado(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

/**
 * Qual das faixas do item é a que se procura.
 *
 * O item quase nunca é a faixa: um 78 do Great 78 Project tem uma música só,
 * mas uma coletânea de netlabel tem quinze -- medido no `NS050`, que traz
 * `Swing Low` na terceira. Devolver o item seria tocar a primeira música de um
 * disco que tem a procurada lá no meio.
 *
 * Com uma faixa só o item já é a resposta: a busca casou título e artista para
 * chegar até aqui, e um 78 varrido costuma vir sem `title` no arquivo.
 */
export function faixaProcurada(faixas: Faixa[], nome: string): Faixa | null {
  if (faixas.length === 0) return null;
  if (faixas.length === 1) return faixas[0];
  return faixaPorNome(faixas, nome);
}

/**
 * A mesma escolha, sem o atalho da faixa única.
 *
 * Existe porque o atalho ali em cima não é uma gentileza, é uma dedução: só
 * vale quando a busca já casou o *título* do item. Na passada do `etree` o
 * título do item é a data e a casa do show, então nada casou por nome antes de
 * chegar aqui -- devolver a faixa só porque é a única seria tocar o que
 * aparecer. Aqui o nome tem de casar.
 */
export function faixaPorNome(faixas: Faixa[], nome: string): Faixa | null {
  const alvo = achatado(nome);
  if (!alvo) return null;
  return (
    /* O igual vem antes do contido: com `Amor` e `Amor de Ninguém` no mesmo
       disco, quem procura `Amor` quer a que se chama `Amor`, mesmo que a
       outra venha primeiro na lista. */
    faixas.find((f) => achatado(f.titulo) === alvo) ??
    faixas.find((f) => {
      const t = achatado(f.titulo);
      /* Contido, para o `(Remastered)` e afins que o acervo pendura no fim.
         Nunca ao contrário: o nome curto cabe dentro de meio disco. */
      return t.length > 0 && t.includes(alvo);
    }) ??
    null
  );
}

/**
 * O cartaz de outra faixa do mesmo item.
 *
 * O `titulo`, o `artista` e a `capaUrl` de um `EmCartaz` vêm do Last.fm, e
 * valem para *uma* faixa: a que foi procurada. Seguir para a segunda música do
 * show sem trocar esses três deixaria a barra anunciando `Sugar Magnolia`
 * enquanto toca `Scarlet Begonias` -- e errado assim não estoura nada, só
 * toca, que é o motivo de esta regra morar aqui, junto das outras que erram
 * caladas.
 *
 * Nas outras faixas quem nomeia é o acervo: o título do arquivo, a autoria do
 * item e a capa do item. É pior do que o Last.fm (`GYMNOPÉDIE No. 1 - Boston
 * Symphony Orchestra`, com a orquestra no meio) e é o que existe.
 *
 * Voltando para a faixa procurada, devolve o cartaz **original**, o mesmo
 * objeto -- não uma cópia igual. Os nomes bons voltam, e quem depende da
 * identidade dele (o aviso ao sistema, que só se remonta quando o cartaz muda)
 * não é acordado à toa.
 */
export function noCartaz(cartaz: EmCartaz, i: number): EmCartaz {
  if (i === cartaz.gravacao.indice) return cartaz;
  const faixa = cartaz.gravacao.faixas[i];
  return {
    gravacao: cartaz.gravacao,
    titulo: faixa.titulo,
    artista: cartaz.gravacao.autoria,
    capaUrl: cartaz.gravacao.capaUrl,
  };
}

/**
 * Se dois nomes de artista são o mesmo artista.
 *
 * Igualdade, nunca "contém" -- e isso é o ponto todo. Quem procura por `Phish`
 * no `etree` recebe `7 Below - A Tribute to Phish`; quem procura por
 * `The Disco Biscuits` recebe `Triumph: A Tribute to the Disco Biscuits`. Os
 * dois *contêm* o nome pedido, e os dois são outra banda tocando as músicas.
 * Foi medido: de oito artistas, dois responderam banda cover no topo.
 *
 * A única diferença de escrita que aparece de verdade é o artigo -- o Last.fm
 * diz `The Smashing Pumpkins` e o acervo diz `Smashing Pumpkins`. Some com ele
 * antes de achatar, no texto cru, para não amputar `Therapy?`.
 *
 * O preço é errar para o lado seguro: `Music By Umphrey's McGee, Capture by
 * marc geanoules` é um show legítimo e é recusado. Não achar gravação é o que
 * já acontece na maioria das faixas; tocar a banda errada, não.
 */
export function mesmoArtista(a: string, b: string): boolean {
  const semArtigo = (t: string) => achatado(t.replace(/^the\s+/i, ''));
  const x = semArtigo(a);
  return x.length > 0 && x === semArtigo(b);
}
